import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import LightTheme from '@app/modules/cop/drawing/themes/light.json';
import { GoogleMap } from '@angular/google-maps';
import { Observable } from 'rxjs';
import { MapLocation, MapMarkerLiteral } from './select-map-location.models';

const DEFAULT_POSITION = { lat: 40.4682, lng: -98.560224 };
const DEFAULT_ZOOM = 18;

@Component({
    selector: 'topo-select-map-location-body',
    templateUrl: './select-location-body.component.html',
    styleUrls: ['./select-location-body.component.scss'],
    standalone: false
})
export class SelectLocationMapBodyComponent implements OnInit, OnDestroy {
  selectedPin: MapMarkerLiteral;
  @ViewChild(GoogleMap, { static: true }) map: GoogleMap;
  @Input() set width(value: string) {
    this.bodyWidth = `calc(${value} - 20px)`;
  }
  @Input() set height(value: string) {
    this.bodyHeight = `calc(${value} - 206px)`;
  }
  @Input() location?: MapLocation;
  @Output() pinLocationChanged = new EventEmitter<MapLocation>();

  center: google.maps.LatLngLiteral;
  zoom: number = DEFAULT_ZOOM;
  options: google.maps.MapOptions = {
    mapTypeControl: true,
    mapTypeControlOptions: {
      position: google.maps.ControlPosition.LEFT_BOTTOM,
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE]
    },
    fullscreenControl: false,
    scrollwheel: true,
    rotateControl: false,
    scaleControl: true,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM
    },
    clickableIcons: true,
    disableDoubleClickZoom: true,
    streetViewControl: false,
    disableDefaultUI: true,
    maxZoom: 20,
    minZoom: 2,
    draggableCursor: 'pointer'
  };
  bodyWidth: string;
  bodyHeight: string;
  selectedAddress: string;
  geocoder: google.maps.Geocoder;
  sendLocation: MapLocation;

  constructor(private ngZone: NgZone, private changeDetector: ChangeDetectorRef) {}

  _hasInitialized = false;

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {}

  private initMapLocation(): void {
    this.selectedAddress = null;
    this.zoom = DEFAULT_ZOOM;
    if (!!this.location?.position?.lat) {
      if (!!this.location?.zoomLevel) {
        this.zoom = this.location?.zoomLevel;
      }
      this.center = { lat: this.location.position.lat, lng: this.location.position.lng };
      this.selectedPin = {
        position: this.location.position,
        options: {
          animation: google.maps.Animation.DROP,
          draggable: true
        }
      };
      this.selectedAddress = this.location.address;
      this.sendLocationChanged();
    }
    if (!!!this.center) {
      this.getCurrentPosition().subscribe({
        next: position => {
          this.center = { lat: position.coords.latitude, lng: position.coords.longitude };
          this.selectedPin = {
            position: { lat: this.center.lat, lng: this.center.lng },
            options: {
              animation: google.maps.Animation.DROP,
              draggable: true
            }
          };
          this.loadSelectedAddress();
        },
        error: error => {}
      });
    }
    if (!!!this.center) {
      this.center = DEFAULT_POSITION;
      this.zoom = DEFAULT_ZOOM;
      this.selectedPin = {
        position: { lat: this.center.lat, lng: this.center.lng },
        options: {
          animation: google.maps.Animation.DROP,
          draggable: true
        }
      };
      this.loadSelectedAddress();
    }

    if (!!this.center) {
      this.zoomToCenterLocation();
    }

    this.changeDetector.detectChanges();
  }

  private getCurrentPosition(): Observable<any> {
    return new Observable(observer => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          position => {
            observer.next(position);
            observer.complete();
          },
          error => {
            observer.error(error);
          }
        );
      } else {
        // not available
      }
    });
  }

  public onDragend(m, event) {
    this.selectedPin = {
      position: event.latLng,
      options: {
        animation: google.maps.Animation.DROP,
        draggable: true,
        title: null
      }
    };

    this.loadSelectedAddress();
  }

  public onMapClick(event) {
    this.selectedPin = {
      position: { lat: event.latLng.lat(), lng: event.latLng.lng() },
      options: {
        animation: google.maps.Animation.DROP,
        draggable: true,
        title: null
      }
    };
    this.loadSelectedAddress();
  }

  public onCenterChange(event) {}

  public onGoToSelected(): void {
    if (!!this.selectedPin?.position) {
      if (!!this.sendLocation?.zoomLevel) {
        this.zoom = this.sendLocation?.zoomLevel;
      }
      this.center = this.selectedPin.position;
      this.zoomToCenterLocation();
      this.changeDetector.detectChanges();
    }
  }

  public onSearchResultChanged(location: MapLocation) {
    this.selectedAddress = location.address;
    if (!!location.zoomLevel) {
      this.zoom = location.zoomLevel;
    }

    const latitude = location.position?.lat;
    const longitude = location.position?.lng;

    this.center = {
      lat: latitude,
      lng: longitude
    };

    this.selectedPin = {
      position: { lat: latitude, lng: longitude },
      options: {
        animation: google.maps.Animation.DROP,
        draggable: true
      }
    };
    this.zoomToCenterLocation();
    this.changeDetector.detectChanges();
    this.sendLocationChanged();
  }

  private zoomToCenterLocation(): void {
    var bounds = new google.maps.LatLngBounds();

    var extendBy = 0.001;
    var point1 = new google.maps.LatLng(this.center.lat + extendBy, this.center.lng + extendBy);
    var point2 = new google.maps.LatLng(this.center.lat - extendBy, this.center.lng - extendBy);
    bounds.extend(point1);
    bounds.extend(point2);
    this.map.fitBounds(bounds);
  }

  private sendLocationChanged(): void {
    if (!!this.selectedPin?.position) {
      const selectedMarker = this.selectedPin as any;

      this.sendLocation = {
        position: {
          lat: typeof selectedMarker.position.lat === 'function' ? selectedMarker.position.lat() : selectedMarker.position.lat,
          lng: typeof selectedMarker.position.lng === 'function' ? selectedMarker.position.lng() : selectedMarker.position.lng
        },
        viewport: this.map.getBounds(),
        zoomLevel: this.map.getZoom(),
        address: this.selectedAddress
      };
      this.pinLocationChanged.emit(this.sendLocation);
    }
  }

  private loadSelectedAddress(): void {
    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }
    this.geocoder.geocode({ location: this.selectedPin.position }, (results, status) => {
      if (status === 'OK' && results.length) {
        this.selectedAddress = results[0].formatted_address;
        this.sendLocationChanged();
      } else {
        this.selectedAddress = null;
        this.sendLocationChanged();
      }
    });
  }

  handleMapInit(): void {
    this.map.mapTypes.set('light-gray', new google.maps.StyledMapType(LightTheme, { name: 'light-gray' }));
    this._hasInitialized = true;
    this.initMapLocation();
  }
}
